/** Past-day weather: Open-Meteo reanalysis (not a forecast or station measurement).
 * The existing Firebase callable remains the only forecast provider.
 * Open-Meteo historical API: https://open-meteo.com/en/docs/historical-weather-api
 */
export type HistoricalWeatherRequest={date:string;query:string;latitude?:number;longitude?:number};
export type HistoricalWeatherRaw={
 date:string;query:string;resolvedName:string;minTemp:number;maxTemp:number;weatherCode:number;
 isHistorical:true;precipitationUnit:"mm";
 hourly:Array<{time:string;temperature:number;apparent:number;precipitation:number;wind:number}>;
};
type DailyPayload={time?:string[];weather_code?:Array<number|null>;temperature_2m_min?:Array<number|null>;temperature_2m_max?:Array<number|null>};
type HourlyPayload={time?:string[];temperature_2m?:Array<number|null>;apparent_temperature?:Array<number|null>;precipitation?:Array<number|null>;wind_speed_10m?:Array<number|null>};
type WeatherPayload={daily?:DailyPayload;hourly?:HourlyPayload};
const isoDay=(date:Date)=>date.toISOString().slice(0,10);
const shifted=(value:string,offset:number)=>{const d=new Date(`${value}T12:00:00Z`);d.setUTCDate(d.getUTCDate()+offset);return isoDay(d);};
async function readJson(url:string,signal?:AbortSignal):Promise<unknown>{
 const ctrl=new AbortController();const timeout=setTimeout(()=>ctrl.abort(),17000);
 const onAbort=()=>ctrl.abort();signal?.addEventListener("abort",onAbort,{once:true});
 try{
  if(signal?.aborted)throw new Error("request-cancelled");
  const response=await fetch(url,{signal:ctrl.signal,headers:{Accept:"application/json"}});
  if(!response.ok)throw new Error(`weather-provider-${response.status}`);
  return response.json();
 }finally{clearTimeout(timeout);signal?.removeEventListener("abort",onAbort);}
}
function validDate(date:string){return /^\d{4}-\d{2}-\d{2}$/.test(date)&&!Number.isNaN(Date.parse(`${date}T12:00:00Z`));}
function parseDays(payload:WeatherPayload,dates:Set<string>,query:string,resolvedName:string,includeHourly:boolean):HistoricalWeatherRaw[]{
 const daily=payload.daily;const hours=payload.hourly;
 if(!Array.isArray(daily?.time)||!Array.isArray(daily?.weather_code)||!Array.isArray(daily?.temperature_2m_min)||!Array.isArray(daily?.temperature_2m_max))return [];
 const byDay=new Map<string,HistoricalWeatherRaw["hourly"]>();
 if(includeHourly&&hours?.time&&hours.temperature_2m&&hours.apparent_temperature&&hours.precipitation&&hours.wind_speed_10m){
  hours.time.forEach((time,index)=>{
   const temperature=hours.temperature_2m?.[index],apparent=hours.apparent_temperature?.[index],precipitation=hours.precipitation?.[index],wind=hours.wind_speed_10m?.[index];
   if([temperature,apparent,precipitation,wind].some(value=>typeof value!=="number"||!Number.isFinite(value)))return;
   const date=time.slice(0,10);if(!dates.has(date))return;
   const list=byDay.get(date)||[];
   list.push({time:time.slice(11,16),temperature:temperature!,apparent:apparent!,precipitation:precipitation!,wind:wind!});byDay.set(date,list);
  });
 }
 return daily.time.flatMap((date,index)=>{
  if(!dates.has(date))return [];
  const minTemp=daily.temperature_2m_min?.[index],maxTemp=daily.temperature_2m_max?.[index],weatherCode=daily.weather_code?.[index];
  if(typeof minTemp!=="number"||!Number.isFinite(minTemp)||typeof maxTemp!=="number"||!Number.isFinite(maxTemp)||typeof weatherCode!=="number"||!Number.isFinite(weatherCode))return [];
  return [{date,query,resolvedName,minTemp,maxTemp,weatherCode,isHistorical:true as const,precipitationUnit:"mm" as const,hourly:byDay.get(date)||[]}];
 });
}
export async function fetchHistoricalWeatherRaw({requests,language,includeHourly=false,signal}:{requests:HistoricalWeatherRequest[];language:string;includeHourly?:boolean;signal?:AbortSignal}):Promise<HistoricalWeatherRaw[]>{
 const today=(()=>{const now=new Date();return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-${String(now.getDate()).padStart(2,"0")}`;})();
 const recentStart=shifted(today,-5);
 const groups=new Map<string,{query:string;dates:Set<string>;lat?:number;lon?:number}>();
 for(const item of requests){
  const query=String(item.query||"").trim().slice(0,110),date=String(item.date||"");
  if(!query||!validDate(date)||date>=today||date<"1940-01-01")continue;
  const hasCoords=typeof item.latitude==="number"&&typeof item.longitude==="number"&&Number.isFinite(item.latitude)&&Number.isFinite(item.longitude)
    &&Math.abs(item.latitude)<=90&&Math.abs(item.longitude)<=180;
  const key=hasCoords?`${query}|${item.latitude?.toFixed(5)}|${item.longitude?.toFixed(5)}`:query;
  if(!groups.has(key))groups.set(key,{query,dates:new Set(),...(hasCoords?{lat:item.latitude,lon:item.longitude}:{})});
  groups.get(key)!.dates.add(date);
 }
 const all:Array<HistoricalWeatherRaw>=[];
 const work=Array.from(groups.values());
 // Limit concurrent calls; a single failed city cannot suppress the others.
 for(let i=0;i<work.length;i+=4){
  if(signal?.aborted)break;
  const batch=await Promise.all(work.slice(i,i+4).map(async({query,dates,lat,lon})=>{
   try{
    // A place picked on the Mundely map already has trusted coordinates: prefer those to fuzzy geocoding.
    let location: {latitude:number;longitude:number;name:string;country?:string}|undefined;
    if(typeof lat==="number"&&typeof lon==="number")location={latitude:lat,longitude:lon,name:query};
    else {
      const geocode=new URLSearchParams({name:query.split(",")[0].trim(),count:"1",language:["it","es","en"].includes(language)?language:"en",format:"json"});
      const geo=await readJson(`https://geocoding-api.open-meteo.com/v1/search?${geocode}`,signal) as {results?:Array<{latitude:number;longitude:number;name:string;country?:string}>};
      location=geo.results?.find(row=>Number.isFinite(row.latitude)&&Number.isFinite(row.longitude));
    }
    if(!location)return [];
    const label=[location.name,location.country].filter(Boolean).join(", ");
    const result:HistoricalWeatherRaw[]=[];
    for(const category of ["archive","recent"] as const){
     const subset=Array.from(dates).filter(date=>category==="archive"?date<recentStart:date>=recentStart).sort();
     if(!subset.length)continue;
     const params=new URLSearchParams({latitude:String(location.latitude),longitude:String(location.longitude),daily:"weather_code,temperature_2m_max,temperature_2m_min",timezone:"auto"});
     if(includeHourly)params.set("hourly","temperature_2m,apparent_temperature,precipitation,wind_speed_10m");
     let url="";
     if(category==="archive"){
      params.set("start_date",subset[0]);params.set("end_date",subset[subset.length-1]);
      url=`https://archive-api.open-meteo.com/v1/archive?${params}`;
     }else{
      params.set("past_days","7");params.set("forecast_days","1");
      url=`https://api.open-meteo.com/v1/forecast?${params}`;
     }
     try{
      const payload=await readJson(url,signal) as WeatherPayload;
      result.push(...parseDays(payload,new Set(subset),query,label,includeHourly));
     }catch{if(signal?.aborted)throw new Error("request-cancelled");}
    }
    return result;
   }catch{return [] as HistoricalWeatherRaw[];}
  }));
  all.push(...batch.flat());
 }
 return all;
}
